import React, { useState } from 'react';
import { useQuery } from '@common/hooks';
import { useCurrentEntity } from '@identity/hooks';
import {
  Link,
  ButtonGroup,
  Drawer,
  ErrorHandler,
  Gutter,
  HistoryDropDown,
} from '@common/components';
import { Button } from 'antd';
import { datetime } from '@common/services/datetime';
import Auth from 'works/legacy/lib/auth';
import { ExtensionOfferAssignmentSummary } from './_extension-offer-assignment-summary';
import { ExtensionOfferHeader } from './_extension-offer-header';
import { ExtensionOfferDetails } from './_extension-offer-details';
import { ExtensionOfferActionAccept } from './_extension-offer-action-accept';
import { ExtensionOfferActionApprove } from './_extension-offer-action-approve';
import {
  ExtensionOfferActionRetract,
  ExtensionOfferChangeResponseDrawer,
} from '@working-experience/components';
import {
  ApprovalChainActionButtons,
  ApprovalChainDecisionSteps,
} from '@approvals/components';
import {
  ChangeRequestDialogBox,
  RetractChangeRequest,
} from '@applicant-flow/components';
import { OfferChangeRequestDrawer } from 'works/legacy/components/offer-change-request-drawer';
import { ExtensionOfferChangeResponseButtons } from './_extension-offer-change-response-footer';
import { getLengthInWeeksValue } from 'works/legacy/services/length-in-weeks-service';
import { toISODateFormat } from 'works/legacy/lib/date-helper';
import {
  EXTENSION_OFFER_QUERY,
  ExtensionOfferType,
} from '@working-experience/graphql';
import { buildDialogUrl } from '@common/services/dialogs-service';
import { getExtensionOfferReasonDescription } from '@working-experience/services';

export function ExtensionOfferDrawer({
  open,
  onClose,
  extensionOfferId,
  offerChangeRequestId,
}: {
  open: boolean;
  onClose: () => void;
  extensionOfferId?: string;
  offerChangeRequestId?: string;
}) {
  const [showChangeRequestDrawer, setShowChangeRequestDrawer] = useState(false);
  const [
    hasChangeRequestDrawerReturnedSuccess,
    setHasChangeRequestDrawerReturnedSuccess,
  ] = useState(false);
  const currentEntity = useCurrentEntity();
  const [showChangeResponseDrawer, setShowChangeResponseDrawer] =
    useState(false);
  const [editExtensionOffer, setEditExtensionOffer] = useState(false);

  const { data, error, loading } = useQuery(EXTENSION_OFFER_QUERY, {
    variables: { id: extensionOfferId || '' },
    skip: !extensionOfferId,
  });

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="ExtensionOfferDrawer"
        messageType="contextual"
      />
    );
  }

  const extensionOffer = data?.extensionOffer;
  const assignment = data?.extensionOffer?.staffingRequestAssignment;

  const isSupplierUser = Auth.supplierMember();
  const hasPendingChangeRequested =
    extensionOffer?.state === 'change_requested';

  const changeRequests = [...(extensionOffer?.offerChangeRequests || [])].sort(
    (e1, e2) =>
      new Date(e1.createdAt).getTime() - new Date(e2.createdAt).getTime()
  );

  const lastRequest = changeRequests[changeRequests.length - 1];
  const canCreateOfferChangeResponse = lastRequest?.canCreateResponse.value;
  const canRequestChange =
    isSupplierUser &&
    extensionOffer?.state === 'active' &&
    !hasPendingChangeRequested &&
    !hasChangeRequestDrawerReturnedSuccess;

  const extensionTerm = extensionOffer?.notVoidedSourceAssignmentTerms?.[0];

  const extensionStartDate =
    extensionTerm?.startDate || toISODateFormat(extensionOffer?.startDate);
  const extensionEndDate = extensionTerm?.endDate || extensionOffer?.endDate;

  const weeksDiff = getLengthInWeeksValue(
    datetime(extensionStartDate),
    datetime(extensionEndDate)
  );

  function getOrganizationUnit() {
    const defaultOrganizationUnit =
      assignment?.staffingRequestApplication?.staffingRequest?.organizationUnit;
    const organizationUnitFromTerm =
      extensionOffer?.lastTermCreatedTillExtensionOfferCreation
        ?.organizationUnit;

    return organizationUnitFromTerm || defaultOrganizationUnit;
  }

  const currentChain = extensionOffer?.approvalChains?.[0];
  const currentApprovers = currentChain?.currentApprovers;

  const displayApprovalChainStatus =
    currentChain &&
    currentEntity.entityType === 'Organization' &&
    !hasPendingChangeRequested;

  const displayApprovalChainActionButtons =
    currentApprovers &&
    currentApprovers.length > 0 &&
    displayApprovalChainStatus &&
    extensionOffer?.state === 'pending';

  const extensionOfferData = {
    ...extensionOffer,
    billingRegularRate:
      extensionTerm?.billingRegularRate || extensionOffer?.billingRegularRate,
    timeoffDescription:
      extensionTerm?.timeoffDescription || extensionOffer?.timeoffDescription,
    shiftType: extensionTerm?.shiftType || extensionOffer?.shiftType,
    startDate: extensionStartDate,
    endDate: extensionEndDate,
    extensionReason: getExtensionOfferReasonDescription(
      extensionOffer?.extensionReason
    ),
    offerChangeRequests: changeRequests,
  } as ExtensionOfferType; // No addition of new fields but only overriding a few values

  const extensionOfferRejectionDrawerUrl = buildDialogUrl(
    'extension-offer-rejection-drawer',
    {
      props: {
        extensionOfferId: extensionOffer?.id || '',
      },
    }
  );

  return (
    <>
      <Drawer
        open={open}
        className="extension-offer-drawer"
        title="Extension Offer"
        onClose={onClose}
        loading={loading}
        footer={
          !hasChangeRequestDrawerReturnedSuccess && (
            <ButtonGroup>
              {extensionOffer?.canRetract.value && (
                <ExtensionOfferActionRetract
                  extensionOfferId={extensionOffer.id}
                  size="large"
                  buttonText="Retract Extension"
                />
              )}
              {extensionOffer?.canReject?.value && (
                <Link to={extensionOfferRejectionDrawerUrl}>
                  <Button
                    danger
                    key="rejectionButton"
                    size="large"
                    type="primary"
                  >
                    Reject
                  </Button>
                </Link>
              )}
              {hasPendingChangeRequested && canCreateOfferChangeResponse && (
                <ExtensionOfferChangeResponseButtons
                  onClick={(editExtensionOffer) => {
                    setShowChangeResponseDrawer(true);
                    setEditExtensionOffer(editExtensionOffer);
                  }}
                />
              )}
              {canRequestChange && (
                <Button
                  key="changeRequestButton"
                  size="large"
                  onClick={() => setShowChangeRequestDrawer(true)}
                >
                  Request Changes
                </Button>
              )}
              {extensionOffer?.canAccept?.value && (
                <ExtensionOfferActionAccept
                  extensionOfferId={extensionOffer.id}
                />
              )}
              {extensionOffer?.canApprove?.value &&
                !displayApprovalChainActionButtons && (
                  <ExtensionOfferActionApprove
                    extensionOfferId={extensionOffer.id}
                  />
                )}
              {displayApprovalChainActionButtons && (
                <ApprovalChainActionButtons
                  approvalChainStepId={currentChain?.currentStep?.id}
                  approveButtonText="Approve"
                  currentApprovers={currentApprovers}
                  rejectionDrawerTitle="Reject"
                />
              )}
            </ButtonGroup>
          )
        }
      >
        {data && (
          <>
            {extensionOffer && assignment && (
              <Gutter direction="bottom" size="md">
                <ExtensionOfferHeader
                  assignment={assignment}
                  offer={extensionOffer}
                />
              </Gutter>
            )}

            {displayApprovalChainStatus && currentChain && (
              <Gutter direction="bottom" size="md">
                <ApprovalChainDecisionSteps
                  approvalChain={currentChain}
                  displayActionButtonsInsideStep={false}
                  displayTitle={false}
                />
              </Gutter>
            )}
            {lastRequest && (
              <Gutter direction="bottom" size="md">
                <ChangeRequestDialogBox changeRequest={lastRequest}>
                  {hasPendingChangeRequested && isSupplierUser && (
                    <RetractChangeRequest changeRequestId={lastRequest.id} />
                  )}
                </ChangeRequestDialogBox>
              </Gutter>
            )}

            {extensionOffer && extensionEndDate && (
              <>
                <ExtensionOfferAssignmentSummary
                  assignment={assignment}
                  billingCard={extensionOffer.billingCard}
                  shiftType={extensionOffer.shiftType}
                  startDate={extensionStartDate}
                  endDate={extensionEndDate}
                  state={extensionOffer.state}
                  billingRegularRate={
                    extensionTerm?.billingRegularRate ||
                    extensionOffer.billingRegularRate
                  }
                  originalBillingRegularRate={
                    extensionOffer.originalBillingRegularRate
                  }
                  organizationUnit={getOrganizationUnit()}
                />

                <ExtensionOfferDetails
                  state={extensionOffer.state || ''}
                  startDate={extensionStartDate}
                  endDate={extensionEndDate}
                  originalEndDate={extensionOffer.originalEndDate || ''}
                  shiftType={
                    extensionTerm?.shiftType || extensionOffer.shiftType || ''
                  }
                  originalShiftType={extensionOffer.originalShiftType || ''}
                  timeoffDescription={
                    extensionTerm?.timeoffDescription ||
                    extensionOffer.timeoffDescription ||
                    ''
                  }
                  extensionReason={extensionOffer.extensionReason || ''}
                  lengthInWeeks={weeksDiff}
                  originalHoursPerShift={extensionOffer.originalHoursPerShift}
                  originalShiftsPerWeek={extensionOffer.originalShiftsPerWeek}
                  hoursPerShift={extensionOffer.hoursPerShift}
                  shiftsPerWeek={extensionOffer.shiftsPerWeek}
                  offerChangeRequests={changeRequests}
                  overtimeThreshold={
                    extensionOffer.overtimeThreshold || undefined
                  }
                />
              </>
            )}
            <HistoryDropDown data={extensionOffer?.history || []} />
          </>
        )}
      </Drawer>
      {assignment && extensionOffer && extensionOfferData && (
        <>
          <ExtensionOfferChangeResponseDrawer
            onDrawerClose={() => setShowChangeResponseDrawer(false)}
            visible={showChangeResponseDrawer}
            assignment={assignment}
            extensionOffer={extensionOfferData}
            edit={editExtensionOffer}
            offerChangeRequestId={offerChangeRequestId}
          />
          <OfferChangeRequestDrawer
            onDrawerClose={() => setShowChangeRequestDrawer(false)}
            onSuccess={() => setHasChangeRequestDrawerReturnedSuccess(true)}
            visible={showChangeRequestDrawer}
            application={assignment.staffingRequestApplication}
            offer={extensionOfferData}
            type="extension_offer_acceptance"
          />
        </>
      )}
      <style jsx>{`
        :global(.ExtensionOfferDrawer) :global(.ant-drawer-body) {
          display: flex;
          flex-direction: column;
        }
      `}</style>
    </>
  );
}
